<script setup>
import { computed, onMounted, ref, reactive, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import PageLayout from 'dashboard/components-next/aifocus/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const agentId = computed(() => Number(route.params.agentId));
const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](agentId.value)
);

const uiFlags = useMapGetter('aifocusAgents/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const isUpdating = computed(() => uiFlags.value.isUpdating);

// Guardrails state
const guardrails = ref([]);

// Modal state
const showAddModal = ref(false);
const editingGuardrail = ref(null);

// Guardrail types available
const guardrailTypes = [
  {
    type: 'max_length',
    name: 'Limite de Caracteres',
    description: 'Limita o tamanho maximo da mensagem de entrada',
    icon: 'i-lucide-text-cursor-input',
    valueType: 'number',
    placeholder: '10000',
    defaultValue: 10000,
  },
  {
    type: 'block_keywords',
    name: 'Bloquear Palavras',
    description: 'Bloqueia mensagens que contem palavras especificas',
    icon: 'i-lucide-ban',
    valueType: 'array',
    placeholder: 'palavra1, palavra2, palavra3',
    defaultValue: [],
  },
  {
    type: 'block_output_keywords',
    name: 'Filtrar Palavras na Resposta',
    description: 'Remove ou redige palavras sensiveis nas respostas do agente',
    icon: 'i-lucide-eye-off',
    valueType: 'array',
    placeholder: 'segredo, senha, confidencial',
    defaultValue: [],
  },
  {
    type: 'require_keywords',
    name: 'Exigir Palavras-chave',
    description: 'Exige que a mensagem contenha pelo menos uma das palavras',
    icon: 'i-lucide-check-circle',
    valueType: 'array',
    placeholder: 'suporte, ajuda, duvida',
    defaultValue: [],
  },
  {
    type: 'language_filter',
    name: 'Filtro de Linguagem',
    description: 'Bloqueia linguagem ofensiva ou inapropriada',
    icon: 'i-lucide-shield-alert',
    valueType: 'boolean',
    defaultValue: true,
  },
  {
    type: 'pii_filter',
    name: 'Filtro de PII',
    description:
      'Detecta e protege informacoes pessoais (CPF, email, telefone)',
    icon: 'i-lucide-user-x',
    valueType: 'boolean',
    defaultValue: true,
  },
  {
    type: 'topic_restriction',
    name: 'Restricao de Topico',
    description: 'Restringe as conversas a topicos especificos',
    icon: 'i-lucide-bookmark',
    valueType: 'array',
    placeholder: 'vendas, suporte, produtos',
    defaultValue: [],
  },
  {
    type: 'max_tokens',
    name: 'Limite de Tokens',
    description: 'Limita o numero de tokens na resposta do agente',
    icon: 'i-lucide-hash',
    valueType: 'number',
    placeholder: '1000',
    defaultValue: 1000,
  },
];

// Form state
const guardrailForm = reactive({
  type: '',
  value: '',
  enabled: true,
});

const handleBackToSettings = () => {
  router.push({
    name: 'aifocus_agents_edit',
    params: {
      accountId: route.params.accountId,
      agentId: route.params.agentId,
    },
  });
};

const updateAgentFromData = agentData => {
  if (!agentData) return;
  guardrails.value = agentData.guardrails || [];
};

onMounted(async () => {
  // Validate agentId before making any API calls
  if (!agentId.value || isNaN(agentId.value)) {
    console.error(
      'guardrails/Index.vue: Invalid agentId on mount:',
      agentId.value
    );
    return;
  }

  await store.dispatch('aifocusAgents/get');
  if (!agent.value) {
    await store.dispatch('aifocusAgents/show', agentId.value);
  }
  updateAgentFromData(agent.value);
});

// Watch agent changes - use Vue watch to avoid infinite loops
watch(
  agent,
  newAgent => {
    if (newAgent) updateAgentFromData(newAgent);
  },
  { deep: false }
);

const getGuardrailInfo = type => {
  return guardrailTypes.find(g => g.type === type) || {};
};

const isGuardrailConfigured = type => {
  return guardrails.value.some(g => g.type === type);
};

const getConfiguredGuardrail = type => {
  return guardrails.value.find(g => g.type === type);
};

const formatValue = guardrail => {
  const info = getGuardrailInfo(guardrail.type);
  if (info.valueType === 'array') {
    return Array.isArray(guardrail.value)
      ? guardrail.value.slice(0, 3).join(', ') +
          (guardrail.value.length > 3 ? ` +${guardrail.value.length - 3}` : '')
      : guardrail.value;
  }
  if (info.valueType === 'boolean') {
    return guardrail.value ? 'Ativado' : 'Desativado';
  }
  return guardrail.value;
};

const openAddModal = (type = null) => {
  if (type) {
    const existing = getConfiguredGuardrail(type);
    const typeInfo = getGuardrailInfo(type);

    if (existing) {
      editingGuardrail.value = existing;
      guardrailForm.type = existing.type;
      guardrailForm.enabled = existing.enabled !== false;

      if (typeInfo.valueType === 'array') {
        guardrailForm.value = Array.isArray(existing.value)
          ? existing.value.join(', ')
          : existing.value || '';
      } else {
        guardrailForm.value = existing.value;
      }
    } else {
      editingGuardrail.value = null;
      guardrailForm.type = type;
      guardrailForm.value = typeInfo.defaultValue;
      guardrailForm.enabled = true;
    }
  } else {
    editingGuardrail.value = null;
    guardrailForm.type = '';
    guardrailForm.value = '';
    guardrailForm.enabled = true;
  }
  showAddModal.value = true;
};

const saveGuardrail = async () => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  try {
    const typeInfo = getGuardrailInfo(guardrailForm.type);

    let value = guardrailForm.value;
    if (typeInfo.valueType === 'array' && typeof value === 'string') {
      value = value
        .split(',')
        .map(s => s.trim())
        .filter(s => s);
    } else if (typeInfo.valueType === 'number') {
      value = parseInt(value, 10);
    }

    const guardrailData = {
      type: guardrailForm.type,
      value,
      enabled: guardrailForm.enabled,
    };

    let newGuardrails;

    if (editingGuardrail.value) {
      newGuardrails = guardrails.value.map(g => {
        if (g.type === editingGuardrail.value.type) {
          return guardrailData;
        }
        return g;
      });
    } else {
      if (isGuardrailConfigured(guardrailForm.type)) {
        useAlert('Este guardrail ja esta configurado');
        return;
      }
      newGuardrails = [...guardrails.value, guardrailData];
    }

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      guardrails: newGuardrails,
    });

    guardrails.value = newGuardrails;
    showAddModal.value = false;
    useAlert('Guardrail salvo com sucesso');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar guardrail');
  }
};

const toggleGuardrail = async guardrail => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  try {
    const newGuardrails = guardrails.value.map(g => {
      if (g.type === guardrail.type) {
        return { ...g, enabled: !g.enabled };
      }
      return g;
    });

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      guardrails: newGuardrails,
    });

    guardrails.value = newGuardrails;
    useAlert(`Guardrail ${guardrail.enabled ? 'desativado' : 'ativado'}`);
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar guardrail');
  }
};

const removeGuardrail = async guardrail => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  const info = getGuardrailInfo(guardrail.type);
  if (!confirm(`Deseja remover o guardrail "${info.name}"?`)) return;

  try {
    const newGuardrails = guardrails.value.filter(
      g => g.type !== guardrail.type
    );

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      guardrails: newGuardrails,
    });

    guardrails.value = newGuardrails;
    useAlert('Guardrail removido');
  } catch (error) {
    useAlert(error?.message || 'Erro ao remover guardrail');
  }
};

const selectedTypeInfo = computed(() => {
  return getGuardrailInfo(guardrailForm.type);
});

const getCategoryColor = type => {
  const colors = {
    max_length: 'bg-blue-100 text-blue-700',
    block_keywords: 'bg-red-100 text-red-700',
    block_output_keywords: 'bg-orange-100 text-orange-700',
    require_keywords: 'bg-green-100 text-green-700',
    language_filter: 'bg-yellow-100 text-yellow-700',
    pii_filter: 'bg-purple-100 text-purple-700',
    topic_restriction: 'bg-indigo-100 text-indigo-700',
    max_tokens: 'bg-cyan-100 text-cyan-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-700';
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #header>
      <div class="flex items-center justify-between w-full px-6 py-4">
        <div class="flex items-center gap-4">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            size="sm"
            @click="handleBackToSettings"
          />
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              Guardrails de Seguranca
            </h2>
            <p class="text-sm text-n-slate-11">
              Configure regras de seguranca para proteger as interacoes do
              agente.
            </p>
          </div>
        </div>
        <Button
          label="Adicionar Guardrail"
          icon="i-lucide-shield-plus"
          @click="openAddModal()"
        />
      </div>
    </template>

    <template #body>
      <div class="px-6 pb-6">
        <!-- Agent Info -->
        <div
          v-if="agent"
          class="mb-6 p-4 bg-n-alpha-1 border border-n-weak rounded-xl"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-bot text-xl text-n-slate-11" />
            </div>
            <div>
              <p class="font-medium text-n-slate-12">{{ agent.name }}</p>
              <p class="text-xs text-n-slate-10">
                {{ guardrails.filter(g => g.enabled !== false).length }}
                guardrails ativos
              </p>
            </div>
          </div>
        </div>

        <!-- Configured Guardrails -->
        <div class="mb-8">
          <h3
            class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-shield-check text-n-slate-11" />
            Guardrails Configurados
          </h3>

          <div
            v-if="guardrails.length === 0"
            class="flex flex-col items-center justify-center py-12 text-center bg-n-alpha-1 border border-dashed border-n-weak rounded-xl"
          >
            <div
              class="w-12 h-12 mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-shield text-2xl text-n-slate-10" />
            </div>
            <h4 class="text-md font-medium text-n-slate-12 mb-1">
              Nenhum guardrail configurado
            </h4>
            <p class="text-sm text-n-slate-11 mb-4 max-w-md">
              Adicione guardrails para proteger as interacoes do seu agente.
            </p>
            <Button
              label="Adicionar Guardrail"
              icon="i-lucide-plus"
              variant="secondary"
              @click="openAddModal()"
            />
          </div>

          <div v-else class="grid gap-3">
            <div
              v-for="guardrail in guardrails"
              :key="guardrail.type"
              class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl"
              :class="{ 'opacity-50': guardrail.enabled === false }"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center"
                  :class="getCategoryColor(guardrail.type)"
                >
                  <span
                    class="text-xl"
                    :class="[getGuardrailInfo(guardrail.type).icon]"
                  />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <p class="font-medium text-n-slate-12">
                      {{ getGuardrailInfo(guardrail.type).name }}
                    </p>
                  </div>
                  <p class="text-xs text-n-slate-10">
                    {{ formatValue(guardrail) }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="guardrail.enabled !== false"
                    class="sr-only peer"
                    @change="toggleGuardrail(guardrail)"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-brand/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand"
                  />
                </label>
                <Button
                  icon="i-lucide-settings"
                  variant="ghost"
                  size="sm"
                  @click="openAddModal(guardrail.type)"
                />
                <Button
                  icon="i-lucide-trash-2"
                  variant="ghost"
                  size="sm"
                  color="ruby"
                  @click="removeGuardrail(guardrail)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Available Guardrails -->
        <div class="mb-8">
          <h3
            class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-list-checks text-n-slate-11" />
            Guardrails Disponiveis
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              v-for="type in guardrailTypes"
              :key="type.type"
              class="flex items-center gap-3 p-4 rounded-xl border text-left transition-colors"
              :class="
                isGuardrailConfigured(type.type)
                  ? 'border-green-300 bg-green-50'
                  : 'border-n-weak hover:border-n-brand hover:bg-n-alpha-1'
              "
              @click="openAddModal(type.type)"
            >
              <div
                class="w-10 h-10 rounded-lg flex items-center justify-center"
                :class="getCategoryColor(type.type)"
              >
                <span class="text-xl" :class="[type.icon]" />
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <p class="font-medium text-n-slate-12">{{ type.name }}</p>
                  <span
                    v-if="isGuardrailConfigured(type.type)"
                    class="i-lucide-check text-green-600"
                  />
                </div>
                <p class="text-xs text-n-slate-10">{{ type.description }}</p>
              </div>
            </button>
          </div>
        </div>

        <!-- Info Card -->
        <div class="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div class="flex gap-3">
            <span
              class="i-lucide-shield-alert text-amber-600 text-xl flex-shrink-0"
            />
            <div>
              <h4 class="font-medium text-amber-900 mb-1">
                Sobre os Guardrails
              </h4>
              <p class="text-sm text-amber-700">
                Guardrails ajudam a manter as conversas seguras e focadas. Eles
                podem filtrar conteudo inapropriado, proteger informacoes
                sensiveis e garantir que o agente permaneca dentro do escopo
                definido.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Add/Edit Guardrail Modal -->
      <div
        v-if="showAddModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showAddModal = false"
      >
        <div
          class="bg-n-solid-2 rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        >
          <div
            class="px-6 py-4 border-b border-n-weak sticky top-0 bg-n-solid-2 z-10"
          >
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ editingGuardrail ? 'Editar' : 'Adicionar' }} Guardrail
            </h3>
            <p class="text-sm text-n-slate-11">
              Configure as regras de seguranca para o agente.
            </p>
          </div>

          <div class="p-6 space-y-4">
            <!-- Type Selection -->
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Tipo de Guardrail
              </label>
              <select
                v-model="guardrailForm.type"
                class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12"
                :disabled="editingGuardrail"
              >
                <option value="">Selecione um tipo</option>
                <option
                  v-for="type in guardrailTypes"
                  :key="type.type"
                  :value="type.type"
                  :disabled="
                    !editingGuardrail && isGuardrailConfigured(type.type)
                  "
                >
                  {{ type.name }}
                  {{ isGuardrailConfigured(type.type) ? '(configurado)' : '' }}
                </option>
              </select>
            </div>

            <!-- Type Description -->
            <div
              v-if="selectedTypeInfo.description"
              class="p-3 bg-n-alpha-1 rounded-lg"
            >
              <p class="text-sm text-n-slate-11">
                {{ selectedTypeInfo.description }}
              </p>
            </div>

            <!-- Value Input -->
            <template v-if="guardrailForm.type">
              <div
                v-if="selectedTypeInfo.valueType === 'number'"
                class="flex flex-col gap-2"
              >
                <label class="text-sm font-medium text-n-slate-12">
                  Valor
                </label>
                <input
                  v-model="guardrailForm.value"
                  type="number"
                  :placeholder="selectedTypeInfo.placeholder"
                  class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12"
                />
              </div>

              <div
                v-else-if="selectedTypeInfo.valueType === 'array'"
                class="flex flex-col gap-2"
              >
                <label class="text-sm font-medium text-n-slate-12">
                  Valores (separados por virgula)
                </label>
                <textarea
                  v-model="guardrailForm.value"
                  rows="3"
                  :placeholder="selectedTypeInfo.placeholder"
                  class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12"
                />
              </div>

              <div
                v-else-if="selectedTypeInfo.valueType === 'boolean'"
                class="flex items-center gap-2"
              >
                <label class="text-sm font-medium text-n-slate-12">
                  Status
                </label>
                <span class="text-sm text-n-slate-11">
                  Este guardrail sera
                  {{ guardrailForm.enabled ? 'ativado' : 'desativado' }}
                </span>
              </div>

              <label class="flex items-center gap-2 text-n-slate-12">
                <input
                  v-model="guardrailForm.enabled"
                  type="checkbox"
                  class="w-4 h-4"
                />
                Guardrail habilitado
              </label>
            </template>
          </div>

          <div
            class="px-6 py-4 border-t border-n-weak flex justify-end gap-3 sticky bottom-0 bg-n-solid-2"
          >
            <Button
              label="Cancelar"
              variant="secondary"
              @click="showAddModal = false"
            />
            <Button
              label="Salvar"
              :disabled="!guardrailForm.type"
              :is-loading="isUpdating"
              @click="saveGuardrail"
            />
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
